import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button } from "theme-ui";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { deleteServiceArea, getServiceAreas, updateServiceArea } from "api/ServiceAreas";
import { trackActivity } from "api/ActivityTracker";

import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { useToast } from "components/toast";
import BasicButton from "components/forms/BasicButton";
import { CheckMarkIcon, QuestionMarkIcon, ServiceAreaIcon, TrashIcon, PencilIcon } from "components/svg";
import TextInput from "components/forms/TextInput";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import DrawGoogleMap from "components/forms/DrawGoogleMap";
import ColorPickerField from "components/forms/ColorPickerField";
import { colorForIndex } from "components/ServiceAreaMap";
import UserContext from "context/User";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";

export default function EditModal({ serviceArea, includeButton, afterUpdate, onToggle, isOpen = true }: { serviceArea: {[k: string]: any}, includeButton?: boolean, isOpen?: boolean; afterUpdate?: () => void, onToggle?: (open: boolean) => void }) {

  const toast = useToast();  
  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
  const { user } = useContext<any>(UserContext);

  // Session 30 — existing service areas fetched on modal open, shown as
  // muted context polygons behind the shape being edited. The current
  // area is excluded via excludeId so DrawGoogleMap's editable overlay
  // doesn't double up with a muted duplicate.
  const [contextPolygons, setContextPolygons] = useState<any[]>([]);
  const [showContext, setShowContext] = useState<boolean>(true);

  // Session 30 hotfix: track whether the user actually redrew or cleared
  // the polygon in this modal session. Combined with the backend fix in
  // service-area.service.ts (only touch geometry when the key is present),
  // this prevents color-only saves from nuking the stored polygon.
  //
  // Root cause: the base list fetch strips geometry to keep the row
  // payload small (Session 30 optimization). The row is passed straight to
  // EditModal, so `values.geometry` starts as undefined. Prior to this
  // fix, save built a payload that either omitted geometry (backend still
  // coerced to null) or sent geometry: undefined, both of which wiped the
  // stored polygon. Now: if the user didn't touch DrawGoogleMap, we omit
  // geometry entirely and the backend leaves it alone.
  const [geometryDirty, setGeometryDirty] = useState<boolean>(false);

  // Reset the dirty flag whenever the modal opens or the target area
  // changes, so a stale flag from a previous edit session can't cause a
  // spurious geometry write.
  useEffect(() => {
    setGeometryDirty(false);
  }, [isModalOpen, serviceArea?.id]);

  const _validate = Yup.object().shape({
    name: Yup.string().required("Service area name is required."),    
  });

  async function _onSubmit(values: any) {  
    // Base payload always includes name and color. Geometry is only
    // included when the user actually touched DrawGoogleMap this session
    // (see geometryDirty). Omitting it makes the backend leave the
    // stored polygon alone; sending it (even as null) overwrites it.
    const update: any = {
      name: values.name,
      color: values.color ?? null,
    };
    if (geometryDirty) {
      update.geometry = values.geometry ?? null;
    }

    const res = await updateServiceArea({ data: serviceArea, update });
    if(res?.id){ 
      toast.add("Service area updated","var(--theme-ui-colors-success)");
      const changedFields: string[] = [];
      if ((serviceArea?.name ?? "") !== (res?.name ?? "")) {
        changedFields.push("name");
      }
      // Geometry activity is only meaningful when the user actually
      // touched the map — don't fabricate a diff by stringifying an
      // unpopulated `serviceArea.geometry` against the freshly loaded
      // hydrated response.
      if (geometryDirty) {
        changedFields.push("geometry");
      }
      if (changedFields.length > 0) {
        void trackActivity({
          module: "service_areas",
          action: "update_service_area",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "service_area",
          entityId: res?.id ?? serviceArea?.id ?? null,
          metadata: {
            changedFields,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
      setIsModalOpen(false);
      if(typeof afterUpdate === 'function') afterUpdate();
    } else{
      toast.add(res?.message ? res.message : "Error updating service area","var(--theme-ui-colors-danger)");
    }  
  }

  const confirmDelete = async () => { 
    setConfirmSubmit(true);   
    const res = await deleteServiceArea({ data: serviceArea });
    if(res?.success){ 
      toast.add("Service area deactivated.","var(--theme-ui-colors-success)"); 
      setIsModalOpen(false);
      if(typeof afterUpdate === 'function') afterUpdate();
    }else{
      toast.add(res?.message ? res.message : "Error deleting service area.","var(--theme-ui-colors-danger)");
    } 
    setConfirmSubmit(false);   
    setConfirmModal(false)          
  };  

  const deleteModal = async () => {
    setConfirmModal(<BasicModal
      title="Deactivate service area?"    
      cancel_click={() => setConfirmModal(false)}
      cancel_text="No, cancel"   
      size={"sm"}
      icon={<QuestionMarkIcon fill={"#818181"} height="30px"/>}
      actions={<>
        <BasicButton
          type="button"    
          scheme="danger"      
          onClick={() => confirmDelete()} 
          $submitting={confirmSubmit}
          $iconHeight="14px"
          $iconWidth="14px"
          $loadingColor="#fff"
          keepText={true}
        >
          {!confirmSubmit && <TrashIcon fill={"#fff"}/>}        
          {"Deactivate"}
        </BasicButton>
      </>}    
    >
      <P>Are you sure you want to deactivate <strong style={{color:"var(--theme-ui-colors-black)"}}>{serviceArea?.name}</strong>?</P>
    </BasicModal>);
  }

  useEffect(() => {
    if(typeof onToggle === 'function') onToggle(isModalOpen);
  }, [isModalOpen]);

  // Session 30 — fetch existing areas (with geometry) once when the modal opens.
  useEffect(() => {
    if (!isModalOpen) return;
    if (contextPolygons.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await getServiceAreas({
          query: { includeGeometry: "true", recordsPer: "500", sortBy: "name", sortDir: "asc" },
        });
        if (!cancelled && Array.isArray(res?.items)) {
          setContextPolygons(res.items);
        }
      } catch {
        // Silent fail — context is optional.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isModalOpen]);

  // Number of context polygons actually visible (excludes the one being edited).
  const visibleContextCount = contextPolygons.filter((p) => p?.id !== serviceArea?.id).length;

  // Session 30 follow-up: derive the auto-palette color for this record
  // from its position in the name-sorted list of all areas. Matches the
  // rotation used by ServiceAreaMap and by the table's color-dot column,
  // so the modal previews the same color that the list shows when no
  // explicit color is saved.
  const fallbackColor = (() => {
    if (!Array.isArray(contextPolygons) || contextPolygons.length === 0) return null;
    const idx = contextPolygons.findIndex((p) => p?.id === serviceArea?.id);
    if (idx < 0) return null;
    return colorForIndex(idx);
  })();

  // Session 30 hotfix: hydrate the serviceArea prop with the geometry
  // that comes back in contextPolygons. The base list fetch strips
  // geometry to keep the row payload small, so DrawGoogleMap would
  // otherwise open with a blank editable overlay even though the area
  // has a stored polygon. We reinject geometry here so the user sees
  // and can modify the existing shape; enableReinitialize on the Formik
  // instance below picks up the change when the fetch completes.
  const hydratedServiceArea = useMemo(() => {
    if (!Array.isArray(contextPolygons) || contextPolygons.length === 0) return serviceArea;
    const current = contextPolygons.find((p: any) => p?.id === serviceArea?.id);
    if (!current?.geometry) return serviceArea;
    return { ...serviceArea, geometry: current.geometry };
  }, [serviceArea, contextPolygons]);


  return (
    <>
      {includeButton && <BasicButton type="button" $iconHeight="10px" scheme="secondaryOutline" styles={{position:"relative", bottom:"2px"}} onClick={() => setIsModalOpen(!isModalOpen)}><PencilIcon/>Edit Service Area</BasicButton>}  
      <Modal size="md" isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)} centered>
        <Formik initialValues={{...hydratedServiceArea}} onSubmit={_onSubmit} validationSchema={_validate} enableReinitialize>
          {({ isSubmitting, values, setFieldValue, submitCount, errors }: { isSubmitting: boolean, values: any, setFieldValue: (name: string, val?: any) => void, submitCount: number, errors: any }) => { 
            return (                
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>
                  <ModalIcon><ServiceAreaIcon fill={"000"}/></ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Edit service area</ModalTitle>
                    <ModalSubtitle>{"Edit a service area that you have created."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>              
                <ModalBody> 
                  <TextInput
                    name="name"
                    label="Service Area Name"
                    value={values.name}
                    onChange={(val: any) => setFieldValue("name",val)}
                    $errors={errors?.name && submitCount > 0 ? errors?.name : null}    
                  />
                  <ColorPickerField
                    label="Map color"
                    value={values.color}
                    onChange={(val: string | null) => setFieldValue("color", val)}
                    fallbackValue={fallbackColor}
                    helperText="Optional. Leave blank to use the automatic color."
                  />
                  {visibleContextCount > 0 && (
                    <div style={{ margin: "8px 0 4px", fontSize: 12, color: "#818181", display: "flex", alignItems: "center", gap: 8 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={showContext}
                          onChange={(e) => setShowContext(e.target.checked)}
                        />
                        Show existing service areas
                      </label>
                      {showContext && (
                        <span>· {visibleContextCount} area{visibleContextCount === 1 ? "" : "s"} shown as context</span>
                      )}
                    </div>
                  )}
                  <DrawGoogleMap 
                    label="Service Area"
                    value={values.geometry}
                    onChange={(val: any) => {
                      setFieldValue("geometry", val);
                      setGeometryDirty(true);
                    }}
                    contextPolygons={showContext ? contextPolygons : []}
                    excludeId={serviceArea?.id}
                  />             
                </ModalBody>
                <ModalFooter>
                  <BasicButton 
                    scheme="dangerSecondary"
                    styles={{marginRight:"auto!important"}}
                    type="button" 
                    onClick={() => deleteModal()}                                        
                  >
                    <TrashIcon fill={"#A60000"}/> Deactivate
                  </BasicButton> 
                  <Button
                    variant="primaryOutline"
                    type="button" 
                    onClick={() => setIsModalOpen(false)} 
                  >Cancel</Button>   
                  <BasicButton
                    type="submit" 
                    $submitting={isSubmitting}
                    $iconHeight="14px"
                    $iconWidth="14px"
                    $loadingColor="#fff"
                    keepText={true}
                  >
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"}/>}
                    Save service area
                  </BasicButton>
                </ModalFooter>            
              </Form>
            );
          }}
        </Formik>
      </Modal>
      {confirmModal}
    </>
  );
}
